import axios from 'axios';
import { UseFormSetError } from 'react-hook-form';
import { Dispatch, SetStateAction } from 'react';
import { TFunction } from 'i18next';

interface ZipTaxResponse {
  rate: number;
  state: string;
}

/**
 * Makes a call to the zipTax API to check if the state is VT.
 * If it is VT and isSelfServiceSubscription is true: shows the VT pricing modal
 * - Otherwise, passes the validation.
 *
 * If an error occurs:
 * - Checks if the isSelfServiceSubscription is true: if yes, passes the validation.
 * - Else, checks if the error code is 404: shows an error message.
 * - Otherwise, shows a service unavailable modal.
 *
 * @param value The value to check
 * @param isSelfServiceSubscription Flag indicating if subscriptions self-service is enabled based on selectedPurchaseType
 * @param setError Function to set an error
 * @param t Translation function
 * @param setShowModal Function to set whether to show a modal
 * @param setServiceIsUnavailable Function to set if the service is unavailable
 */
export const validateZipCode = async (
  value: string,
  isSelfServiceSubscription: boolean,
  setError: UseFormSetError<any>,
  t: TFunction<any, any>,
  setShowModal: Dispatch<SetStateAction<boolean>>,
  setServiceIsUnavailable: Dispatch<SetStateAction<boolean>>,
): Promise<string | undefined> => {
  const url = `/genesis/v1/tax_rate?zip=${value}`;

  try {
    const response = await axios.get<ZipTaxResponse>(url);

    if (isSelfServiceSubscription && response.data.state === 'VT') {
      const errorMessage = t('businessZipCode.vermont');
      setError('businessZipCode', {
        type: 'custom',
        message: t('businessZipCode.vermont'),
      });
      setShowModal(true);
      return errorMessage;
    }

    return;
  } catch (error) {
    if (error.response?.status === 404) {
      const errorMessage = t('businessZipCode.invalid');
      setError('businessZipCode', {
        type: 'custom',
        message: t('businessZipCode.invalid'),
      });
      return errorMessage;
    }

    if (!isSelfServiceSubscription) return;

    setShowModalAndServiceUnavailable();
    return '';
  }

  function setShowModalAndServiceUnavailable() {
    setShowModal(true);
    setServiceIsUnavailable(true);
  }
};
